import axios from 'axios';
import { apiWithContext } from './api';
import {
  InventoryCountRequest,
  InventoryCountEntry,
  InventoryCountApprovalItem,
  InventoryCountRequestsResponse,
  InventoryCountRequestDetailsResponse,
  InventoryCountApprovalResponse,
  CreateInventoryCountRequest,
  CreateInventoryCountEntry,
  ApproveInventoryCountRequest
} from '../types/inventoryCount';

// الحصول على قائمة طلبات جرد المستودعات
export const getInventoryCountRequests = async (page = 1, limit = 10) => {
  try {
    const response = await apiWithContext.get(`inventory-count/requests?page=${page}&limit=${limit}`, 'inventory-count');
    return response.data as InventoryCountRequestsResponse;
  } catch (error) {
    console.error('Error fetching inventory count requests:', error);
    throw error;
  }
};

// الحصول على تفاصيل طلب جرد محدد
export const getInventoryCountRequestDetails = async (requestId: string) => {
  try {
    const response = await axios.get(`/api/inventory-count/requests/${requestId}/details`);
    return response.data as InventoryCountRequestDetailsResponse;
  } catch (error) {
    console.error('Error fetching inventory count request details:', error);
    throw error;
  }
};

// إنشاء طلب جرد مستودع جديد
export const createInventoryCountRequest = async (data: CreateInventoryCountRequest) => {
  try {
    const response = await axios.post('/api/inventory-count/requests', data);
    return response.data;
  } catch (error) {
    console.error('Error creating inventory count request:', error);
    throw error;
  }
};

// إضافة إدخال جرد جديد
export const createInventoryCountEntry = async (data: CreateInventoryCountEntry) => {
  try {
    // الحصول على رمز المصادقة من التخزين المحلي
    const token = localStorage.getItem('token');
    
    const response = await axios.post(
      `/api/inventory-count/requests/${data.requestId}/entries`,
      data,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error creating inventory count entry:', error);
    throw error;
  }
};

// الحصول على تفاصيل إدخال جرد محدد
export const getInventoryCountEntry = async (entryId: string) => {
  try {
    const response = await axios.get(`/api/inventory-count/entries/${entryId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching inventory count entry:', error);
    throw error;
  }
};

// تحديث إدخال جرد
export const updateInventoryCountEntry = async (entryId: string, data: Partial<CreateInventoryCountEntry>) => {
  try {
    const response = await axios.put(`/api/inventory-count/entries/${entryId}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating inventory count entry:', error);
    throw error;
  }
};

// حذف إدخال جرد
export const deleteInventoryCountEntry = async (entryId: string) => {
  try {
    const response = await axios.delete(`/api/inventory-count/entries/${entryId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting inventory count entry:', error);
    throw error;
  }
};

// الحصول على عناصر اعتماد الجرد
export const getInventoryCountApprovalItems = async (requestId: string) => {
  try {
    const response = await apiWithContext.get(`inventory-count/requests/${requestId}/approval`);
    return response.data as InventoryCountApprovalResponse;
  } catch (error) {
    console.error('Error fetching inventory count approval items:', error);
    throw error;
  }
};

// اعتماد طلب جرد
export const approveInventoryCount = async (data: ApproveInventoryCountRequest) => {
  try {
    console.log('بيانات طلب الاعتماد:', JSON.stringify(data, null, 2));
    
    // تسجيل العناصر المضافة يدويًا للتشخيص
    const manualItems = data.items.filter(item => item.manuallyAdded === true);
    console.log(`عدد العناصر المضافة يدويًا: ${manualItems.length}`);
    if (manualItems.length > 0) {
      console.log('تفاصيل العناصر المضافة يدويًا:', manualItems);
    }
    
    // تأكد من أن خاصية manuallyAdded منقولة بشكل صحيح كقيمة boolean
    const requestData = {
      ...data,
      items: data.items.map(item => ({
        ...item,
        // تحويل صريح إلى قيمة boolean للتأكد من معالجتها بشكل صحيح
        manuallyAdded: item.manuallyAdded === true ? true : false
      }))
    };
    
    // Step 1: Prepare the approval - تحديث البيانات المباشرة في الاعتماد
    const approvalResponse = await apiWithContext.post(`inventory-count/requests/${data.requestId}/approval`, {
      items: requestData.items
    });
    
    if (!approvalResponse.data || !approvalResponse.data.success) {
      throw new Error('فشل في تحديث بيانات الاعتماد');
    }
    
    // Step 2: Prepare the approval
    const prepareResponse = await apiWithContext.post(`inventory-count/requests/${data.requestId}/approve/prepare`);
    
    if (!prepareResponse.data || !prepareResponse.data.success || !prepareResponse.data.approval) {
      throw new Error('فشل في تحضير بيانات الاعتماد');
    }
    
    // Step 3: Finalize the approval
    const approvalId = prepareResponse.data.approval._id;
    const finalizeResponse = await apiWithContext.post(`inventory-count/approvals/${approvalId}/finalize`);
    
    // تعديل تنسيق الاستجابة لتتضمن newEntry
    return {
      success: true,
      message: 'تم اعتماد الجرد وتحديث كميات المخزون بنجاح',
      data: finalizeResponse.data,
      newEntry: finalizeResponse.data.newEntry
    };
  } catch (error) {
    console.error('Error approving inventory count:', error);
    throw error;
  }
};

// إلغاء اعتماد طلب جرد
export const cancelInventoryCountApproval = async (requestId: string, reason?: string) => {
  try {
    const response = await apiWithContext.post(`inventory-count/requests/${requestId}/cancel-approval`, {
      reason
    });
    return {
      success: true,
      message: 'تم إلغاء اعتماد الجرد وإعادة كميات المخزون بنجاح',
      data: response.data
    };
  } catch (error) {
    console.error('Error cancelling inventory count approval:', error);
    throw error;
  }
};

// حذف طلب جرد
export const cancelInventoryCountRequest = async (requestId: string, reason?: string) => {
  try {
    const response = await axios.delete(`/api/inventory-count/requests/${requestId}`, {
      data: { reason }
    });
    return {
      success: true,
      message: response.data.message
    };
  } catch (error: any) {
    console.error('Error cancelling inventory count request:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'حدث خطأ أثناء حذف طلب الجرد'
    };
  }
};

// تحديث حالة طلب الجرد
export const updateInventoryCountStatus = async (requestId: string, status: string, reason?: string) => {
  try {
    const response = await axios.put(`/api/inventory-count/requests/${requestId}/status`, {
      status,
      reason
    });
    return {
      success: true,
      request: response.data
    };
  } catch (error) {
    console.error('Error updating inventory count status:', error);
    throw error;
  }
};

// الحصول على العناصر التي لها كمية غير صفرية في المستودع ولم يتم إدراجها في إدخالات الجرد
export const getMissingInventoryItems = async (requestId: string) => {
  try {
    console.log(`جاري جلب العناصر غير المدرجة لطلب الجرد: ${requestId}`);
    const response = await apiWithContext.get(`inventory-count/requests/${requestId}/missing-items`);
    
    // طباعة الاستجابة للتشخيص
    console.log('استجابة API العناصر غير المدرجة:', response.data);
    
    // إذا كانت الاستجابة تحتوي على items، نستخدمها، وإلا نستخدم missingItems إذا كانت موجودة
    if (response.data && Array.isArray(response.data.items)) {
      console.log(`تم العثور على ${response.data.items.length} عنصر غير مدرج`);
      return response.data;
    } else if (response.data && Array.isArray(response.data.missingItems)) {
      console.log(`تم العثور على ${response.data.missingItems.length} عنصر غير مدرج (تنسيق قديم)`);
      // تحويل الاستجابة القديمة إلى التنسيق الجديد
      return {
        ...response.data,
        items: response.data.missingItems
      };
    } else {
      console.log('لم يتم العثور على عناصر غير مدرجة');
      return { success: true, items: [] };
    }
  } catch (error) {
    console.error('Error fetching missing inventory items:', error);
    throw error;
  }
};

// البحث عن متغيرات المنتجات بواسطة الباركود
export const searchProductVariantsByBarcode = async (barcode: string) => {
  try {
    // إرسال الباركود في body بدلاً من URL لتجنب مشاكل الترميز مع الأحرف الخاصة مثل /
    // استخدام السياق لجرد المستودعات
    const response = await apiWithContext.post(`/products/search-by-barcode`, { barcode }, 'inventory-count');
    console.log('API response for barcode search:', response.data);
    
    // تنسيق البيانات المرجعة لتتوافق مع التنسيق المتوقع في الواجهة
    if (response.data.success && response.data.product && response.data.variant) {
      const product = response.data.product;
      const variant = response.data.variant;
      
      // البحث عن الباركود المطابق
      const matchedBarcode = variant.barcodes.find(b =>
        b.code === barcode || (b.additionalCodes && Array.isArray(b.additionalCodes) && b.additionalCodes.includes(barcode))
      );
      
      // تحويل خصائص المتغير من Map إلى كائن إذا لزم الأمر
      let variantAttributes = variant.attributes || {};
      if (variantAttributes instanceof Map) {
        variantAttributes = Object.fromEntries(variantAttributes);
      } else if (typeof variantAttributes !== 'object') {
        variantAttributes = {};
      }
      
      const formattedResponse = {
        success: true,
        product: product,
        variant: {
          productId: product._id,
          productName: product.name,
          variantId: variant._id,
          variantAttributes: variantAttributes,
          barcode: matchedBarcode?.code || barcode,
          unitId: (matchedBarcode?.unit || variant.unit || ''),
          unitName: (matchedBarcode?.unit || variant.unit || 'قطعة')
        }
      };
      
      console.log('Formatted response for frontend:', formattedResponse);
      return formattedResponse;
    }
    return response.data;
  } catch (error) {
    console.error('Error searching product variants by barcode:', error);
    throw error;
  }
};

// البحث المجمّع عن متغيرات المنتجات بعدة باركودات دفعة واحدة (مقسّم إلى دفعات)
export const searchProductVariantsByBarcodes = async (barcodes: string[]) => {
  const resultsMap = new Map<string, any>();
  const CHUNK_SIZE = 500;

  for (let start = 0; start < barcodes.length; start += CHUNK_SIZE) {
    const chunk = barcodes.slice(start, start + CHUNK_SIZE);
    const response = await apiWithContext.post(
      `/inventory/search-barcodes`,
      { barcodes: chunk },
      'inventory-count',
      { timeout: 120000 }
    );

    const results: any[] = response.data?.results || [];
    results.forEach((r: any) => {
      if (r && r.barcode) {
        resultsMap.set(r.barcode.toString().trim().toLowerCase(), r);
      }
    });
  }

  return resultsMap;
};

// البحث عن متغيرات المنتجات
export const searchProductVariants = async (query: string, limit = 20) => {
  try {
    const response = await apiWithContext.get(`/products/variants/search?q=${query}&limit=${limit}`, 'inventory-count');
    return response.data;
  } catch (error) {
    console.error('Error searching product variants:', error);
    throw error;
  }
};

// تصدير بيانات الجرد المعتمدة إلى ملف Excel
export const exportInventoryApprovalToExcel = async (requestId: string) => {
  try {
    const response = await axios.get(`/api/inventory-count/requests/${requestId}/export-excel`, {
      responseType: 'blob'
    });
    
    // إنشاء رابط تنزيل للملف
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `inventory-count-${requestId}.xlsx`);
    document.body.appendChild(link);
    link.click();
    
    // تنظيف بعد التنزيل
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
    
    return true;
  } catch (error) {
    console.error('Error exporting inventory count to Excel:', error);
    throw error;
  }
};

// الحصول على المستودعات المتاحة لإنشاء طلبات الجرد
export const getAvailableWarehouses = async () => {
  try {
    const response = await apiWithContext.get('inventory-count/warehouses', 'inventory-count');
    return response.data;
  } catch (error) {
    console.error('Error fetching available warehouses for inventory count:', error);
    throw error;
  }
};